import React, { PropsWithChildren, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import { Container, FlexContainer } from "../Containers";
import { ColorClassNames, ColorVariables } from "../Colors";
import { CircledQuestionIcon, LockIcon } from "../Images/icons";
import "text-security";
import { OptionalFunction } from "../types";
import { baseLength } from "../Styles/globals";
import type { InputProps } from "./Input";
import { Popup } from "../Popup";

const StyledContainer = styled(Container)<{ type: ErrorWrapperProps["type"]; error: boolean }>`
  input {
    ${(props) => {
      // Dropdowns already have an error property
      if (props.type === "select") return;
      if (props.error) {
        return `border: solid 2px ${ColorVariables.cherry7} !important`;
      }
    }}
  }
`;

const StyledCaption = styled.p<{ color: string }>`
  color: ${(props) => props.color};
`;

export type ErrorWrapperProps = PropsWithChildren<
  Partial<{
    active: boolean;
    title: string;
    showLock: boolean;
    onIconClick: OptionalFunction;
    error: boolean;
    caption: string;
    captionColor: string;
    captionAlign: string;
    onCaptionClick: OptionalFunction;
    required: boolean;
    className: string;
    type?: InputProps["type"];
    helperTitle: string;
    helperBody: JSX.Element;
    subtitle?: string;
    errorMessagePosition?: string;
  }> & {
    name: string;
  }
>;

const ErrorWrapper = ({
  children = undefined,
  active = false,
  title = "",
  subtitle = "",
  showLock = false,
  error = false,
  caption = "",
  captionColor = "",
  name = uuid(),
  captionAlign = undefined,
  onCaptionClick = undefined,
  required = false,
  className = "",
  type,
  helperTitle,
  helperBody,
  errorMessagePosition = "bottom left",
}: ErrorWrapperProps) => {
  const [showHelperPopup, setShowHelperPopup] = useState(false);
  const showQuestion = helperTitle || helperBody;

  const renderCaption = (textPosition: string) => {
    return (
      caption?.length > 0 && (
        <FlexContainer
          data-name="captionContainer"
          className="fullWidth"
          justifyContent={
            captionAlign ||
            (textPosition.includes("right") ? "flex-end" : textPosition.includes("center") ? "center" : "flex-start")
          }
          maxWidth={baseLength * 150}
          mobileMaxWidth={baseLength * 82.5}
          marginRight={24}
          paddingLeft={baseLength * 2}
          paddingRight={baseLength * 2}
          cursor={onCaptionClick ? "pointer" : undefined}
          onClick={onCaptionClick}
          marginTop={baseLength}
        >
          <StyledCaption
            data-name="caption"
            className="small"
            color={captionColor || (error ? ColorVariables.cherry9 : ColorVariables.mint)}
          >
            {caption}
          </StyledCaption>
        </FlexContainer>
      )
    );
  };

  return (
    <Container data-name="errorWrapper" className={`errorWrapper fullWidth input-form ${className}`}>
      <Popup
        content={helperBody}
        handlePopup={() => setShowHelperPopup(false)}
        header={helperTitle}
        showPopup={showHelperPopup}
      />
      {errorMessagePosition && errorMessagePosition?.includes("top") && renderCaption(errorMessagePosition)}
      <StyledContainer
        className="fullWidth"
        maxWidth={baseLength * 150}
        mobileMaxWidth={baseLength * 82.5}
        error={error}
        data-error={error}
        type={type}
        data-name={type === "select" || type === "autocomplete" ? name : undefined}
      >
        {(title || required || showLock || showQuestion || subtitle) && (
          <label data-name="label" htmlFor={name}>
            <p
              data-name="title"
              style={{
                color: active ? ColorVariables.mint : ColorVariables.coco5,
                fontWeight: active ? 600 : 400,
                paddingLeft: baseLength * 3,
                marginBottom: baseLength * 3,
              }}
              className={`medium ${ColorClassNames.mint}`}
            >
              {title}
              {required && (
                <span data-name="requiredAsterisk" className={ColorClassNames.mint}>
                  *
                </span>
              )}{" "}
              {showLock && <LockIcon data-name="lockIcon" />}{" "}
              {showQuestion && (
                <button
                  data-name="showQuestionButton"
                  className="clickable"
                  style={{
                    position: "relative",
                    top: "2px",
                    width: "auto",
                  }}
                  type="button"
                  onClick={() => setShowHelperPopup(true)}
                >
                  <CircledQuestionIcon />
                </button>
              )}
            </p>
            {subtitle && (
              <p className="subtitle small" style={{ paddingLeft: baseLength * 3, marginBottom: 12 }}>
                {subtitle}
              </p>
            )}
          </label>
        )}
        <Container position="relative">{children}</Container>
      </StyledContainer>
      {errorMessagePosition && errorMessagePosition.includes("bottom") && renderCaption(errorMessagePosition)}
    </Container>
  );
};

export default ErrorWrapper;
